import React, { useEffect, useRef } from 'react'
import { FlexSectionHeader } from '../style'
import styled from 'styled-components'

import {
  Typography,
  Button,
  List,
  Space,
  Tag,
  Dropdown,
  Menu,
  Modal,
  Spin
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import { PlusOutlined } from '@ant-design/icons'
import { STUDENT } from '../../constants/userRoles'
import { deleteExam, getAllExams } from '../../reducers/examReducer'
import { useNavigate, useParams } from 'react-router'

import { AiOutlineSolution } from 'react-icons/ai'

import useCoursePrivilege from '../../hooks/useCourseprivilege'
import { Link } from 'react-router-dom'

const { Title, Text } = Typography

const Exams = () => {
  const dispatch = useDispatch()
  const { courseId } = useParams()
  const navigate = useNavigate()
  const { enrolled, privilege } = useCoursePrivilege()

  const exams = useSelector((state) => state.exams.data)
  const loading = useSelector((state) => state.exams.loading)
  const transitionTimerIds = useRef([])

  useEffect(() => {
    const fetchExams = () => {
      dispatch(getAllExams(courseId))
    }
    fetchExams()
    const generalPollingInterval = 300000
    const intervalId = setInterval(fetchExams, generalPollingInterval)
    return () => clearInterval(intervalId)
  }, [courseId, dispatch])

  useEffect(() => {
    transitionTimerIds.current.forEach((id) => clearTimeout(id))
    transitionTimerIds.current = []

    if (!exams || exams.length === 0) {
      return
    }

    const now = new Date()

    exams.forEach((exam) => {
      const startTime = new Date(exam.openAt)
      const endTime = new Date(exam.closeAt)

      if (exam.status.code === 'willOpen' && now < startTime) {
        const timeUntilOpen = startTime.getTime() - now.getTime()
        if (timeUntilOpen > 0) {
          const id = setTimeout(() => {
            dispatch(getAllExams(courseId))
          }, timeUntilOpen + 2000)
          transitionTimerIds.current.push(id)
        }
      }

      if (exam.status.code === 'open' && now < endTime) {
        const timeUntilClose = endTime.getTime() - now.getTime()
        if (timeUntilClose > 0) {
          const id = setTimeout(() => {
            dispatch(getAllExams(courseId))
          }, timeUntilClose + 2000)
          transitionTimerIds.current.push(id)
        }
      }
    })

    return () => {
      transitionTimerIds.current.forEach((id) => clearTimeout(id))
      transitionTimerIds.current = []
    }
  }, [exams, courseId, dispatch])

  const activeExams = exams.filter((exam) => exam.status.code !== 'closed')
  const doneExams = exams.filter((exam) => exam.status.code === 'closed')

  return (
    <>
      <FlexSectionHeader>
        <Title level={3}>All Exams</Title>
        {enrolled && privilege !== STUDENT && (
          <Button
            onClick={() => navigate(`/app/course/${courseId}/exams/create`)}
            type="dashed"
            shape="round"
            icon={<PlusOutlined />}
          >
            Add Exam
          </Button>
        )}
      </FlexSectionHeader>

      <div style={{ marginTop: '16px' }}>
        <Text style={{ fontSize: '16px', fontWeight: '600', color: '#595959' }}>
          Active
        </Text>

        <Spin spinning={loading}>
          <List
            dataSource={activeExams}
            renderItem={(exam) => <ExamItem exam={exam} />}
            locale={{ emptyText: 'No active exams found.' }}
          />
        </Spin>
      </div>

      <div
        style={{
          marginTop: '46px'
        }}
      >
        <Text style={{ fontSize: '16px', fontWeight: '600', color: '#595959' }}>
          Done
        </Text>

        <Spin spinning={loading}>
          <List
            dataSource={doneExams}
            renderItem={(exam) => <ExamItem exam={exam} disabled={true} />}
            locale={{ emptyText: 'No completed exams found.' }}
          />
        </Spin>
      </div>
    </>
  )
}

const StyledListItem = styled(List.Item)`
  background-color: #fafafa;
  padding: 24px 16px;
  width: 100%;
  margin-top: 16px;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background-color: #d9d9d9;
  }
`
const ExamItem = ({ exam, disabled }) => {
  const { courseId } = useParams()
  const { privilege } = useCoursePrivilege()
  const dispatch = useDispatch()

  const optionMenu = (
    <Menu>
      <Menu.Item>
        <Link to={`/app/course/${courseId}/assessment/${exam.id}/grade`}>
          Grade All
        </Link>
      </Menu.Item>
      <Menu.Item>
        <Link to={`/app/course/${courseId}/assessment/${exam.id}/submissions`}>
          All Submissions
        </Link>
      </Menu.Item>
      <Menu.Item danger onClick={() => dispatch(deleteExam(courseId, exam.id))}>
        Delete
      </Menu.Item>
    </Menu>
  )

  const handleExamClick = () => {
    if (exam.status.code === 'closed') {
      Modal.info({
        title: 'Exam has ended',
        content: 'This exam is closed and cannot be accessed anymore.',
        okText: 'Ok'
      })
    } else {
      window.open(`/app/course/${courseId}/exam/${exam.id}`)
    }
  }

  const getActions = (privilege) => {
    if (privilege !== STUDENT)
      return (
        <>
          <Space
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            <Dropdown.Button
              trigger={['click']}
              placement="bottomLeft"
              type="text"
              overlay={optionMenu}
            ></Dropdown.Button>
          </Space>
        </>
      )
  }

  return (
    <StyledListItem
      style={{ padding: '12px' }}
      onClick={handleExamClick}
      extra={getActions(privilege)}
    >
      <Space style={{ display: 'flex', alignItems: 'flex-start' }}>
        <AiOutlineSolution
          style={{
            fontSize: '32px',
            color: disabled ? '#a7a7a7d9' : 'intial'
          }}
        />
        <Space size="small" direction="vertical">
          <Title
            style={{ margin: 0, color: disabled ? '#a7a7a7d9' : 'intial' }}
            level={5}
          >
            {exam.title}
          </Title>

          <div>
            {exam.status.code === 'willOpen' && (
              <Tag color="geekblue">Will Open</Tag>
            )}
            {exam.status.code === 'open' && <Tag color="green">Open</Tag>}
            {exam.status.code === 'closed' && <Tag color="red">Closed</Tag>}
            <Text type="secondary">{exam.status.message}</Text>
          </div>

          <Space>
            <span>
              <Text type="secondary">Weightage: </Text>
              <Text strong>{`${exam.weight * 100}%`}</Text>
            </span>
            <span>
              <Text type="secondary">Max Score: </Text>
              <Text strong>{exam.maxScore}</Text>
            </span>
            <span>
              <Text type="secondary">Duration: </Text>
              <Text strong>
                {Math.round(exam.timeLimit / 60)}
                <Text type="secondary"> minutes</Text>
              </Text>
            </span>
            <span>
              <Text type="secondary">Submission: </Text>
              <Text strong>
                {exam.submissionType === 'online' ? 'Online' : 'Written'}
              </Text>
            </span>
          </Space>
        </Space>
      </Space>
    </StyledListItem>
  )
}

export { default as AssessmentCreation } from './components/AssessmentCreation'
export { default as Submissions } from './components/Submissions'

export default Exams
